import { Alert, AlertColor, Snackbar } from '@mui/material'
import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

type SnackbarContextType = {
	addErrorSnackbar: (message?: string) => void
	addSnackbar: (message: string, type?: AlertColor) => void
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined)

export const SnackbarProvider = ({ children }: PropsWithChildren) => {
	const { formatMessage } = useIntl()
	const [open, setOpen] = useState(false)
	const [message, setMessage] = useState('')
	const [type, setType] = useState<AlertColor>('success')
	const [duration, setDuration] = useState(5000)

	const addSnackbar = useCallback((message: string, type: AlertColor = 'success', duration: number = 5000) => {
		setMessage(message)
		setType(type)
		setDuration(duration)
		setOpen(true)
	}, [])

	const addErrorSnackbar = useCallback((message: string = 'global.errors.occurred') => {
		setMessage(formatMessage({ id: message }))
		setType('error')
		setDuration(duration)
		setOpen(true)
	}, [duration])

	const handleClose = () => setOpen(false)

	const value = useMemo(() => ({
		addErrorSnackbar,
		addSnackbar
	}), [addSnackbar])

	return (
		<SnackbarContext.Provider value={value}>
			{children}
			<Snackbar
				open={open}
				autoHideDuration={duration}
				onClose={handleClose}
			>
				<Alert
					severity={type}
					onClose={handleClose}
					variant="filled"
					sx={{
						alignItems: 'center'
					}}
				>
					{message}
				</Alert>
			</Snackbar>
		</SnackbarContext.Provider>
	)
}

/**
 * Snackbar context
 */
export const useSnackbar = () => {
	const context = useContext(SnackbarContext)
	if (!context) throw new Error('useSnackbar must be used within a SnackbarProvider')
	return context
}
